// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { UserServiceRequest } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useParties } from '../../../contexts/PartiesContext';
import { useCreateSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import { ERR_COL_REQ_USER_SVC, SUC_COL_REQ_USER_SVC } from '../../../utils/strings';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestUserServiceDialog = ({ open, onClose }: DialogProps) => {
  const { operator: predefinedOperator } = useParties();
  const [operator, setOperator] = useState<string>(predefinedOperator);
  const party = useModuleParty();

  const { submit: requestUserService } = useCreateSubmission(
    UserServiceRequest,
    SUC_COL_REQ_USER_SVC,
    ERR_COL_REQ_USER_SVC,
  );

  const resetState = useCallback(() => {
    setOperator('');
  }, [setOperator]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    const res = await requestUserService({ argument: { user: party, operator } });
    handleClose();
    return res;
  }, [requestUserService, party, handleClose, operator]);

  const isLoading = false;
  if (isLoading) return null;

  const disableSubmit = !party || !operator;

  return (
    <FormDialog
      open={open}
      title="Request User Service"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput disabled label="Operator ID" value={operator} setValue={setOperator} required />
    </FormDialog>
  );
};
